import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { localizePlayerScheduleItem } from '../../shared/eventTimezones';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  CssBaseline,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import PaymentIcon from '@mui/icons-material/Payment';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import TuneIcon from '@mui/icons-material/Tune';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { supabase } from '../config/supabase';
import { useAuth } from '../auth/AuthContext';
import { formatTime } from '../utils/timeFormat';
import { createPortalTheme, defaultPortalBranding, normalizePortalBranding, type PortalBranding } from '../utils/portalBranding';
import { updatePlayerScheduleResponse } from '../utils/playerScheduleResponse';
import { getPlayerCalendarStatus, getPlayerResponseStatus, type PlayerCalendarStatus } from '../utils/playerCalendarStatus';
import { teamAccentColor } from '../utils/teamColors';
import type { TimeFormat } from '../types';
import { AccountManagement } from './AccountManagement';
import { PlayerPortalRosters } from './PlayerPortalRosters';

type PlayerTeammate = { id: string; name: string };
type PlayerGroups = Record<string, PlayerTeammate[]>;
type PlayerLineup = {
  lines: Array<{
    positionCode: string;
    displayName: string;
    lineType: string | null;
    date?: string | null;
    warmupTime?: string | null;
    startTime?: string | null;
    facilityId?: string | null;
    locationText?: string | null;
    courtAssignment?: string | null;
    hasOverride?: boolean;
    wasRescheduled?: boolean;
    players: PlayerTeammate[];
  }>;
  standby: PlayerTeammate[];
};
type CalendarSubscription = {
  id: string;
  delivery_email: string;
  subscribed: boolean;
  initial_sent_at: string | null;
  last_sent_at: string | null;
  last_sequence: number;
  last_delivery_status: string | null;
  last_delivery_error: string | null;
};
type PlayerMatch = {
  id: string;
  kind: 'match';
  teamId: string;
  teamIds?: string[];
  teamName: string;
  teamShortName?: string | null;
  teamNames?: string[];
  teamColor: string;
  opponent: string;
  date: string;
  time: string | null;
  warmupTime: string | null;
  arrivalTime?: string | null;
  homeAway: string;
  facility: string;
  facilityMapUrl: string;
  location: string;
  selfServiceEnabled?: boolean;
  calendarEnabled?: boolean;
  calendarSubscription?: CalendarSubscription | null;
  ownAvailability: 'available' | 'maybe' | 'not_available' | 'unknown';
  availabilityCounts: Record<string, number>;
  availabilityGroups: PlayerGroups;
  lineupStatus: string | null;
  isTentativeLineup?: boolean;
  lineup: PlayerLineup | null;
  isPlayerInVisibleLineup: boolean;
  isPlayerPlaying: boolean;
  isPlayerTentative?: boolean;
  playerAssignment: {
    positionCode: string;
    displayName: string;
    partnerNames: string[];
    date?: string | null;
    warmupTime?: string | null;
    startTime?: string | null;
    facility?: string;
    location?: string;
    courtAssignment?: string | null;
    hasOverride?: boolean;
    wasRescheduled?: boolean;
  } | null;
};
type PlayerEvent = {
  id: string;
  kind: 'event';
  teamId: string;
  teamIds?: string[];
  teamName: string;
  teamShortName?: string | null;
  teamNames?: string[];
  audienceScope?: 'workspace' | 'selected_teams';
  audienceLabel?: 'Workspace Event' | 'Team Event';
  teamColor: string;
  type: string;
  title: string;
  description: string;
  date: string;
  time: string | null;
  endTime: string | null;
  facility: string;
  facilityMapUrl: string;
  location: string;
  selfServiceEnabled?: boolean;
  calendarEnabled?: boolean;
  calendarSubscription?: CalendarSubscription | null;
  ownRsvp: 'Going' | 'Maybe' | 'Not Going' | 'No Response';
  allowMaybeRsvp: boolean;
  rsvpDeadlineAt: string | null;
  rsvpClosed: boolean;
  ownGuestCount: number;
  rsvpCounts: Record<string, number>;
  rsvpGroups: PlayerGroups;
  guestGroups: PlayerGroups;
  guestSummary: { guests: number; totalAttending: number; maybeGuests: number; potentialAdditional: number };
};
type PlayerHistoryMatch = {
  matchId: string;
  teamName: string;
  seasonName: string;
  date: string;
  opponent: string;
  homeAway: string;
  teamResult: string;
  teamScore: string;
  line: string;
  lineType: string;
  partnerNames: string[];
  playerResult: string;
  scores: string;
};
type PlayerRecord = {
  wins: number;
  losses: number;
  singlesWins: number;
  singlesLosses: number;
  doublesWins: number;
  doublesLosses: number;
};
type PlayerFeeSummary = {
  id: string;
  name: string;
  color: string;
  workspaceId: string;
  fees: Array<{ id: string; date: string | null; description: string; amount: number; status: string }>;
  outstandingFees: number;
  accountCredit: number;
  creditAppliedForDisplay: number;
  amountDue: number;
  remainingCredit: number;
  paymentMethods: Array<{ id: string; method: string; identifier: string }>;
};
type PlayerContext = {
  enabled: boolean;
  linked: boolean;
  error?: string;
  player: { id: string; displayName: string; email: string | null };
  user: { id: string; displayName: string; role: string; isPlayerOnly: boolean };
  teams: Array<{ id: string; name: string; shortName?: string | null; color: string; seasonLabel: string; selfServiceEnabled?: boolean; tentativeLineupsEnabled?: boolean }>;
  featureControls?: {
    playerEventSelfServiceEnabled: boolean;
    playerCalendarEventsEnabled: boolean;
  };
  calendar?: {
    email: string;
    verified: boolean;
    verifiedAt: string | null;
  };
  branding: unknown;
  playingPreferences: {
    values: { singlesPreference: 'yes' | 'if_possible' | 'no'; consecutiveDayTolerance: 'no' | 'if_needed' | 'yes'; sameDayTolerance: 'no' | 'if_needed' | 'yes'; minimumRecoveryHours: number };
    relevance: { singlesDoubles: boolean; sameDay: boolean; consecutiveDays: boolean; recoveryWindow: boolean };
  };
  schedule: { matches: PlayerMatch[]; events: PlayerEvent[]; lineups?: PlayerMatch[] };
  history: {
    matches: PlayerHistoryMatch[];
    records: {
      currentTeams: PlayerRecord;
      allTime: PlayerRecord;
      byTeamSeason: Array<PlayerRecord & { teamName: string; seasonName: string }>;
    };
    partners: Array<{ playerId: string; partnerName: string; matches: number; wins: number; losses: number; currentTeamMatches: number; previousTeamMatches: number }>;
  };
  fees: PlayerFeeSummary[];
};

type PlayerViewProps = {
  timeFormat: TimeFormat;
  onExit?: () => void;
  exitLabel?: string;
};

type ScheduleItem = PlayerMatch | PlayerEvent;
type ScheduleViewMode = 'list' | 'calendar';
type Snack = { severity: 'success' | 'warning' | 'error' | 'info'; message: string };
type PlayerPortalTab = 'schedule' | 'lineups' | 'rosters' | 'preferences' | 'history' | 'fees' | 'account';

const availabilityLabels: Record<PlayerMatch['ownAvailability'], string> = {
  available: 'Available',
  maybe: 'Maybe',
  not_available: 'Not Available',
  unknown: 'No Response',
};

const groupLabels: Record<string, string> = {
  available: 'Available',
  maybe: 'Maybe',
  not_available: 'Not Available',
  unknown: 'No Response',
  going: 'Going',
  notGoing: 'Not Going',
  noResponse: 'No Response',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Date TBD';
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDeadline(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}

function dateParts(value: string): { year: number; month: number; day: number } {
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
}

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function eventTypeIcon(item: ScheduleItem) {
  const sx = { color: teamAccentColor(item.teamColor), fontSize: 19, flexShrink: 0 };
  if (item.kind === 'match') return <SportsTennisIcon sx={sx} aria-label="Match" />;
  if (item.type === 'Practice') return <GroupsIcon sx={sx} aria-label="Practice" />;
  if (item.type === 'Social') return <CelebrationIcon sx={sx} aria-label="Social" />;
  return <CalendarMonthIcon sx={sx} aria-label={item.type || 'Event'} />;
}

function responseDisplay(item: ScheduleItem): { symbol: string; label: string } {
  const status = getPlayerResponseStatus(item);
  if (status === 'available') return { symbol: '✓', label: 'Available' };
  if (status === 'not_available') return { symbol: '✕', label: 'Not Available' };
  if (status === 'going') return { symbol: '✓', label: 'Going' };
  if (status === 'not_going') return { symbol: '✕', label: 'Not Going' };
  if (status === 'maybe') return { symbol: '?', label: 'Maybe' };
  return { symbol: '—', label: 'No Response' };
}

function scheduleTitle(item: ScheduleItem): string {
  return item.kind === 'match' ? `vs ${item.opponent}` : item.title || item.type;
}

function shortDate(value: string): string {
  return formatDate(value).replace(/, \d{4}$/, '');
}

// A match's player-facing time is the expected arrival time. Keep this shared
// by Schedule and Lineups so the two views can never disagree.
function playerVisibleStartTime(item: ScheduleItem): string | null {
  return item.kind === 'match' ? item.arrivalTime || item.warmupTime || item.time : item.time;
}

function PlayerPortalDateTime({ item, timeFormat }: { item: ScheduleItem; timeFormat: TimeFormat }) {
  const time = playerVisibleStartTime(item);
  return (
    <Box sx={{ width: 64, flexShrink: 0 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, color: 'text.secondary' }}>
        {shortDate(item.date)}
      </Typography>
      {time && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatTime(time, timeFormat)}</Typography>}
    </Box>
  );
}

function MatchTimes({ match, timeFormat }: { match: PlayerMatch; timeFormat: TimeFormat }) {
  const matchTime = match.time ? formatTime(match.time, timeFormat) : '';
  const warmupTime = match.warmupTime ? formatTime(match.warmupTime, timeFormat) : '';
  if (warmupTime && matchTime) return <Typography variant="body2">Warmup {warmupTime} · Match {matchTime}</Typography>;
  if (warmupTime) return <Typography variant="body2">Warmup {warmupTime}</Typography>;
  if (matchTime) return <Typography variant="body2">Match {matchTime}</Typography>;
  return null;
}

function accessibleScheduleLabel(item: ScheduleItem, timeFormat: TimeFormat): string {
  const response = responseDisplay(item);
  const type = item.kind === 'match' ? 'Match' : item.type;
  const assignment = item.kind === 'match' && item.isPlayerPlaying ? '. Playing in this match' : item.kind === 'match' && item.isPlayerTentative ? '. Planned for this match' : '';
  return `${type}: ${scheduleTitle(item)} for ${item.teamName} on ${formatDate(item.date)} at ${formatTime(playerVisibleStartTime(item), timeFormat) || 'time TBD'}. Your response: ${response.label}${assignment}.`;
}

function TeamLabel({ name, color }: { name: string; color?: string | null }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: color ? teamAccentColor(color) : 'grey.400',
          border: '1px solid rgba(0,0,0,0.25)',
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" noWrap>{name}</Typography>
    </Stack>
  );
}

function statusColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  if (['available', 'Going', 'Won', 'Win'].includes(status)) return 'success';
  if (['maybe', 'Maybe'].includes(status)) return 'warning';
  if (['not_available', 'Not Going', 'Lost', 'Loss'].includes(status)) return 'error';
  return 'default';
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ? { Authorization: `Bearer ${data.session.access_token}` } : {};
}

function applyAuthenticatedPlayerMetadata(branding: PortalBranding): () => void {
  const iconUrl = branding.portalIconUrl || `${window.location.origin}/courtcaptain-icon.svg`;
  const title = `${branding.displayName} - My Player View`;
  document.title = title;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', branding.primaryColor);
  document.querySelector<HTMLMetaElement>('meta[name="application-name"]')?.setAttribute('content', branding.appName);
  document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', branding.shortName);
  document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')?.setAttribute('href', iconUrl);
  document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.setAttribute('href', iconUrl);
  return () => undefined;
}

function RecordSummary({ title, record }: { title: string; record: PlayerRecord }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 180 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 900 }}>{record.wins}-{record.losses}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Singles {record.singlesWins}-{record.singlesLosses} · Doubles {record.doublesWins}-{record.doublesLosses}
      </Typography>
    </Paper>
  );
}

function ParticipationSummary({ counts, event }: { counts: Record<string, number>; event: boolean }) {
  const rows = event
    ? [['going', '✅', 'Going'], ['maybe', '❓', 'Maybe'], ['notGoing', '❌', 'Not Going'], ['noResponse', 'NR', 'No Response']]
    : [['available', '✅', 'Available'], ['maybe', '❓', 'Maybe'], ['not_available', '❌', 'Not Available'], ['unknown', 'NR', 'No Response']];
  return <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.5 }}>
    {rows.map(([key, symbol, label]) => <Box key={key} aria-label={`${counts[key] || 0} players ${label.toLowerCase()}`} sx={{ minWidth: 0, px: 0.25, display: 'flex', justifyContent: 'center', fontWeight: 800, whiteSpace: 'nowrap' }}>{symbol} {counts[key] || 0}</Box>)}
  </Box>;
}

function ParticipationSection({
  title,
  counts,
  event,
  guestSummary,
  onOpen,
}: {
  title: 'Team Availability' | 'Team RSVPs' | 'Workspace RSVPs';
  counts: Record<string, number>;
  event: boolean;
  guestSummary?: PlayerEvent['guestSummary'];
  onOpen: () => void;
}) {
  const accessibleTitle = title === 'Team Availability' ? 'View team availability' : 'View team RSVPs';
  return (
    <Box>
      <Button
        size="small"
        variant="text"
        aria-label={accessibleTitle}
        onClick={onOpen}
        endIcon={<VisibilityIcon sx={{ fontSize: 20 }} />}
        sx={{ minHeight: 36, px: 0, mb: 0.25, fontWeight: 900, textTransform: 'none', color: 'text.primary', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}
      >
        {title}
      </Button>
      <ParticipationSummary counts={counts} event={event} />
      {event && guestSummary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {guestSummary.guests} Guests · {guestSummary.totalAttending} Total Attending
          {guestSummary.potentialAdditional > 0 ? ` · ${guestSummary.potentialAdditional} Potential Maybe` : ''}
        </Typography>
      )}
    </Box>
  );
}

function ParticipationDetails({ open, onClose, title, subtitle, groups, guestGroups, keys, expandedKey }: { open: boolean; onClose: () => void; title: string; subtitle: string; groups: PlayerGroups; guestGroups?: PlayerGroups; keys: string[]; expandedKey?: string }) {
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="participation-detail-title"><DialogTitle id="participation-detail-title">{title}</DialogTitle><DialogContent><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>{keys.map((key) => {
    const guests = guestGroups?.[key] || [];
    return <Accordion key={key} defaultExpanded={expandedKey === key} disableGutters elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&::before': { display: 'none' } }}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 800 }}>{key === 'available' || key === 'going' ? '✅' : key === 'maybe' ? '❓' : key === 'not_available' || key === 'notGoing' ? '❌' : 'NR'} {groupLabels[key] || key} ({groups[key]?.length || 0}{guests.length ? ` + ${guests.length} guests` : ''})</Typography></AccordionSummary><AccordionDetails sx={{ pt: 0 }}><Typography variant="body2">{groups[key]?.length || guests.length ? [...(groups[key] || []), ...guests].map((player) => player.name).join(', ') : 'No players.'}</Typography></AccordionDetails></Accordion>;
  })}</DialogContent></Dialog>;
}

function TeamContext({ names, show }: { names?: string[]; show: boolean }) {
  if (!show || !names?.length) return null;
  return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>{names.join(' · ')}</Typography>;
}

function MapsLocation({ facility, location, mapUrl }: { facility: string; location: string; mapUrl: string }) {
  const label = facility || location || 'Location TBD';
  const destination = mapUrl || (location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}` : '');
  if (!destination) return <Typography variant="body2" color="text.secondary">{label}</Typography>;
  return <Button component="a" href={destination} target="_blank" rel="noreferrer" startIcon={<LocationOnIcon />} sx={{ px: 0, minHeight: 36, textTransform: 'none', justifyContent: 'flex-start', maxWidth: '100%' }}>{label}</Button>;
}

function EventDescription({ description }: { description: string }) {
  if (!description.trim()) return null;

  return (
    <Box component="section" aria-label="Description" sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>Description</Typography>
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
      >
        {description}
      </Typography>
    </Box>
  );
}

function isPlaying(match: PlayerMatch) {
  return match.isPlayerPlaying;
}

function ScheduleItemPill({ item, timeFormat, compact = false, onOpen }: { item: ScheduleItem; timeFormat: TimeFormat; compact?: boolean; onOpen: (item: ScheduleItem) => void }) {
  return (
    <Button
      size="small"
      variant="text"
      aria-label={accessibleScheduleLabel(item, timeFormat)}
      onClick={() => onOpen(item)}
      sx={{
        justifyContent: 'flex-start',
        minWidth: 0,
        maxWidth: '100%',
        px: compact ? 0.5 : 1,
        py: 0.25,
        borderLeft: `4px solid ${teamAccentColor(item.teamColor)}`,
        bgcolor: 'background.paper',
        color: 'text.primary',
        textTransform: 'none',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
        {eventTypeIcon(item)}
        {!compact && <Typography variant="caption" sx={{ flexShrink: 0 }}>{formatTime(item.time, timeFormat) || 'TBD'}</Typography>}
        <Typography variant="caption" noWrap sx={{ minWidth: 0, flex: 1 }}>{scheduleTitle(item)}</Typography>
      </Stack>
    </Button>
  );
}

function calendarStatusDetails(item: ScheduleItem): { status: PlayerCalendarStatus; label: string } | null {
  const status = getPlayerCalendarStatus(item);
  if (status === 'none') return null;
  const eventName = scheduleTitle(item);
  if (status === 'playing') return { status, label: `Playing ${eventName}` };
  if (status === 'tentative') return { status, label: `Planned for ${eventName}` };
  const response = responseDisplay(item);
  const relation = item.kind === 'match' ? 'for' : 'to';
  return { status, label: `${response.label} ${relation} ${eventName}` };
}

function TennisBallIcon({ tentative = false }: { tentative?: boolean }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        width: 16,
        height: 16,
        borderRadius: '50%',
        bgcolor: tentative ? 'info.main' : 'success.main',
        overflow: 'hidden',
        flexShrink: 0,
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: -2,
          width: 8,
          height: 20,
          border: '1.5px solid white',
          borderTopColor: 'transparent',
          borderBottomColor: 'transparent',
          borderRadius: '50%',
        },
        '&::before': { left: -4, transform: 'rotate(-18deg)' },
        '&::after': { right: -4, transform: 'rotate(-18deg)' },
      }}
    />
  );
}

function PlayingAssignmentIndicator() {
  return (
    <Tooltip title="Playing: assigned to a published lineup" arrow>
      <Box component="span" aria-label="Playing: assigned to a published lineup" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <TennisBallIcon />
      </Box>
    </Tooltip>
  );
}

function TentativeAssignmentIndicator() {
  return (
    <Tooltip title="Planned: assigned to a tentative lineup" arrow>
      <Box component="span" aria-label="Planned: assigned to a tentative lineup" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <TennisBallIcon tentative />
      </Box>
    </Tooltip>
  );
}

function CalendarStatusIndicator({ item }: { item: ScheduleItem }) {
  const detail = calendarStatusDetails(item);
  if (!detail) return null;
  const styleByStatus: Record<Exclude<PlayerCalendarStatus, 'none'>, { symbol?: string; color: string }> = {
    playing: { color: 'success.main' },
    tentative: { color: 'info.main' },
    available: { symbol: '✓', color: 'success.main' },
    going: { symbol: '✓', color: 'success.main' },
    maybe: { symbol: '?', color: 'warning.dark' },
    not_available: { symbol: '✕', color: 'error.main' },
    not_going: { symbol: '✕', color: 'error.main' },
  };
  const style = styleByStatus[detail.status as Exclude<PlayerCalendarStatus, 'none'>];
  return (
    <Tooltip title={detail.label} arrow>
      <Box
        component="span"
        aria-label={detail.label}
        title={detail.label}
        sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: style.color, minWidth: 16, height: 18, fontWeight: 900, fontSize: '1rem', lineHeight: 1, flexShrink: 0 }}
      >
        {detail.status === 'playing' ? <TennisBallIcon /> : detail.status === 'tentative' ? <TennisBallIcon tentative /> : style.symbol}
      </Box>
    </Tooltip>
  );
}

function CalendarEventTypeIcon({ item }: { item: ScheduleItem }) {
  const sx = { color: teamAccentColor(item.teamColor), fontSize: 19, flexShrink: 0 };
  if (item.kind === 'match') return <SportsTennisIcon sx={sx} aria-hidden="true" />;
  if (item.type === 'Practice') return <GroupsIcon sx={sx} aria-hidden="true" />;
  if (item.type === 'Social') return <CelebrationIcon sx={sx} aria-hidden="true" />;
  return <CalendarMonthIcon sx={sx} aria-hidden="true" />;
}

function CalendarEventMarker({ item, timeFormat, onOpen }: { item: ScheduleItem; timeFormat: TimeFormat; onOpen: (item: ScheduleItem) => void }) {
  const status = calendarStatusDetails(item);
  return (
    <Button
      size="small"
      variant="text"
      onClick={() => onOpen(item)}
      aria-label={accessibleScheduleLabel(item, timeFormat)}
      sx={{ minWidth: 0, minHeight: 28, width: '100%', px: 0.25, py: 0, justifyContent: 'flex-start', color: 'text.primary' }}
    >
      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ minWidth: 0 }}>
        <CalendarEventTypeIcon item={item} />
        {status && <CalendarStatusIndicator item={item} />}
      </Stack>
    </Button>
  );
}

function PlayerPortalTeamLegend({ items, teamFilter }: { items: ScheduleItem[]; teamFilter: string }) {
  const teams = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; shortName: string | null; color: string }>();
    items.forEach((item) => {
      if (!byId.has(item.teamId)) byId.set(item.teamId, { id: item.teamId, name: item.teamName, shortName: item.teamShortName || null, color: teamAccentColor(item.teamColor) });
    });
    return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name));
  }, [items]);
  if (teamFilter !== 'all' || teams.length < 2) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap role="list" aria-label="Team color key" sx={{ px: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 900, pt: 0.25 }}>Teams</Typography>
      {teams.map((team) => (
        <Stack key={team.id} direction="row" spacing={0.5} alignItems="center" role="listitem" aria-label={`${team.name}, team color ${team.color}`}>
          <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: team.color, flexShrink: 0 }} />
          <Typography variant="caption" noWrap>{team.shortName || team.name}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function PlayerLineupAssignmentKey() {
  return <>
    <Stack direction="row" spacing={0.4} alignItems="center"><TennisBallIcon /><Typography variant="caption">Playing</Typography></Stack>
    <Stack direction="row" spacing={0.4} alignItems="center"><TennisBallIcon tentative /><Typography variant="caption">Planned</Typography></Stack>
  </>;
}

function PlayerPortalScheduleKey() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap aria-label="Schedule icon key" sx={{ px: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 900 }}>Key</Typography>
      <Stack direction="row" spacing={0.4} alignItems="center"><GroupsIcon sx={{ fontSize: 19 }} aria-hidden="true" /><Typography variant="caption">Practice</Typography></Stack>
      <Stack direction="row" spacing={0.4} alignItems="center"><SportsTennisIcon sx={{ fontSize: 19 }} aria-hidden="true" /><Typography variant="caption">Match</Typography></Stack>
      <PlayerLineupAssignmentKey />
      <Typography variant="caption" color="text.secondary">✓ Available / Going · ? Maybe · ✕ Not Available / Not Going · NR No Response</Typography>
    </Stack>
  );
}

function PlayerPortalLineupsKey() {
  return <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap aria-label="Lineup assignment key" sx={{ px: 0.25 }}>
    <Typography variant="caption" sx={{ fontWeight: 900 }}>Key</Typography>
    <PlayerLineupAssignmentKey />
  </Stack>;
}

function CalendarControls({
  item,
  onSendCalendar,
  onStopCalendar,
}: {
  item: ScheduleItem;
  onSendCalendar?: (item: ScheduleItem) => void;
  onStopCalendar?: (item: ScheduleItem) => void;
}) {
  if (!item.calendarEnabled) return null;
  const subscription = item.calendarSubscription;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
      {subscription?.subscribed && (
        <>
          <Chip size="small" color={subscription.last_delivery_status === 'failed' ? 'error' : 'success'} label={`Calendar event sent to ${subscription.delivery_email}`} />
          <Button size="small" color="inherit" onClick={() => onStopCalendar?.(item)}>Stop Calendar Updates</Button>
        </>
      )}
    </Stack>
  );
}

function SendToMeButton({ item, onSendCalendar }: { item: ScheduleItem; onSendCalendar?: (item: ScheduleItem) => void }) {
  if (!item.calendarEnabled) return null;
  return <Button size="small" variant="outlined" startIcon={<CalendarMonthIcon />} onClick={(event) => { event.stopPropagation(); onSendCalendar?.(item); }} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>Send to me</Button>;
}

function InlineResponseControls({
  item,
  onRespond,
  busy = false,
}: {
  item: ScheduleItem;
  onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void;
  busy?: boolean;
}) {
  const choices = item.kind === 'match'
    ? [
      ['available', 'Available'],
      ['maybe', 'Maybe'],
      ['not_available', 'Not Available'],
    ]
    : item.allowMaybeRsvp === false ? [
      ['Going', 'Going'],
      ['Not Going', 'Not Going'],
    ] : [
      ['Going', 'Going'],
      ['Maybe', 'Maybe'],
      ['Not Going', 'Not Going'],
    ];
  const selectedStatus = item.kind === 'match' ? item.ownAvailability : item.ownRsvp;

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        ...(item.kind === 'event' && item.allowMaybeRsvp === false
          ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
          : {}),
        gap: 0.5,
        width: { xs: '100%', md: 330 },
        maxWidth: '100%',
        flexShrink: 0,
      }}
    >
      {choices.map(([value, label]) => (
        <Button
          key={value}
          size="small"
          color={value === 'not_available' || value === 'Not Going' ? 'error' : value === 'maybe' || value === 'Maybe' ? 'warning' : 'success'}
          variant={selectedStatus === value ? 'contained' : 'outlined'}
          aria-pressed={selectedStatus === value}
          disabled={busy || (item.kind === 'event' && item.rsvpClosed)}
          onClick={(event) => {
            event.stopPropagation();
            onRespond?.(item, value);
          }}
          sx={{
            minWidth: 0,
            px: { xs: 0.5, sm: 0.75 },
            fontSize: { xs: '0.68rem', sm: '0.78rem' },
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span">{label}</Box>
        </Button>
      ))}
      {busy && <CircularProgress size={16} aria-label="Saving response" sx={{ gridColumn: '1 / -1', justifySelf: 'center' }} />}
    </Box>
  );
}

function EventGuestControls({ event, onRespond, busy }: { event: PlayerEvent; onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void; busy: boolean }) {
  const canBringGuests = event.ownRsvp === 'Going' || (event.ownRsvp === 'Maybe' && event.allowMaybeRsvp !== false);
  const guestCount = Number(event.ownGuestCount || 0);
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      onClick={(clickEvent) => clickEvent.stopPropagation()}
      sx={{ mt: 1 }}
    >
      <FormControlLabel
        control={(
          <Checkbox
            size="small"
            checked={guestCount > 0}
            disabled={!canBringGuests || busy || event.rsvpClosed}
            onChange={(change) => onRespond?.(event, event.ownRsvp, change.target.checked ? Math.max(1, guestCount) : 0)}
          />
        )}
        label="Bringing a Guest"
        sx={{ m: 0 }}
      />
      {guestCount > 0 && (
        <TextField
          size="small"
          type="number"
          label="Number of Guests"
          value={guestCount}
          disabled={!canBringGuests || busy || event.rsvpClosed}
          slotProps={{ htmlInput: { min: 1, max: 10, step: 1, inputMode: 'numeric' } }}
          onChange={(change) => {
            const count = Number(change.target.value);
            if (Number.isInteger(count) && count >= 1 && count <= 10) onRespond?.(event, event.ownRsvp, count);
          }}
          sx={{ width: { xs: '100%', sm: 170 } }}
        />
      )}
      {!canBringGuests && <Typography variant="caption" color="text.secondary">{event.ownRsvp === 'Maybe' && event.allowMaybeRsvp === false ? 'Confirm Going to update guests.' : `Choose Going${event.allowMaybeRsvp === false ? '' : ' or Maybe'} to add guests.`}</Typography>}
    </Stack>
  );
}

function responseHeaderContent(item: ScheduleItem, onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void, busy = false) {
  if (item.selfServiceEnabled) return <InlineResponseControls item={item} onRespond={onRespond} busy={busy} />;
  const response = responseDisplay(item);
  return <Box component="span" aria-label={response.label} title={response.label} sx={{ fontWeight: 900, fontSize: '1.05rem' }}>{response.symbol}</Box>;
}

function headerKeyboardToggle(event: React.KeyboardEvent<HTMLElement>, toggle: () => void) {
  if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
  event.preventDefault();
  toggle();
}

function HeaderExpandButton({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  return (
    <Button
      aria-label={expanded ? 'Collapse details' : 'Expand details'}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      sx={{ minWidth: 36, width: 36, height: 36, p: 0, color: 'text.primary' }}
    >
      <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
    </Button>
  );
}

function HeaderActions({
  item,
  expanded,
  onToggle,
  onRespond,
  busy = false,
}: {
  item: ScheduleItem;
  expanded: boolean;
  onToggle: () => void;
  onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void;
  busy?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 42px',
        columnGap: 1.5,
        alignItems: 'center',
        width: { xs: '100%', md: 390 },
        maxWidth: '100%',
        flexShrink: 0,
      }}
    >
      {responseHeaderContent(item, onRespond, busy)}
      <Box sx={{ pl: 0.5, borderLeft: '1px solid', borderColor: 'divider' }}>
        <HeaderExpandButton expanded={expanded} onToggle={onToggle} />
      </Box>
    </Box>
  );
}

function MatchCard({
  match,
  timeFormat,
  defaultExpanded = false,
  onRespond,
  onSendCalendar,
  onStopCalendar,
  activeTeamCount = 1,
  onViewLineup,
  busy = false,
}: {
  match: PlayerMatch;
  timeFormat: TimeFormat;
  defaultExpanded?: boolean;
  onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void;
  onSendCalendar?: (item: ScheduleItem) => void;
  onStopCalendar?: (item: ScheduleItem) => void;
  activeTeamCount?: number;
  onViewLineup?: (match: PlayerMatch) => void;
  busy?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [participationKey, setParticipationKey] = useState<string | null>(null);
  const response = responseDisplay(match);
  return (
    <Card variant="outlined" sx={{ borderLeft: `5px solid ${teamAccentColor(match.teamColor)}` }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} match ${match.opponent}. ${response.label}${isPlaying(match) ? '. Playing in this match.' : match.isPlayerTentative ? '. Planned for this match.' : ''}`}
          onClick={() => setExpanded((current) => !current)}
          onKeyDown={(event) => headerKeyboardToggle(event, () => setExpanded((current) => !current))}
          sx={{ cursor: 'pointer', color: 'text.primary', textAlign: 'left', p: 1.25, minHeight: 56, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ minWidth: 0, width: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <PlayerPortalDateTime item={match} timeFormat={timeFormat} />
              {eventTypeIcon(match)}
              <Typography variant="body2" noWrap sx={{ fontWeight: 900, minWidth: 0, flex: 1 }}>{scheduleTitle(match)}</Typography>
              {isPlaying(match) && <PlayingAssignmentIndicator />}
              {match.isPlayerTentative && <TentativeAssignmentIndicator />}
            </Stack>
            <HeaderActions item={match} expanded={expanded} onToggle={() => setExpanded((current) => !current)} onRespond={onRespond} busy={busy} />
          </Stack>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Stack spacing={1.25}>
              <Divider />
              <Box>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}><TeamContext names={match.teamNames || [match.teamName]} show={activeTeamCount > 1} /></Box>
                  <SendToMeButton item={match} onSendCalendar={onSendCalendar} />
                </Stack>
                <MatchTimes match={match} timeFormat={timeFormat} />
                <MapsLocation facility={match.facility} location={match.location} mapUrl={match.facilityMapUrl} />
                {match.playerAssignment?.hasOverride && (
                  <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{match.playerAssignment.wasRescheduled ? 'Your line was rescheduled.' : 'Your line has an alternate schedule.'}</Typography>
                    <Typography variant="caption" component="div">
                      {[match.playerAssignment.date, match.playerAssignment.warmupTime ? `Warmup ${formatTime(match.playerAssignment.warmupTime, timeFormat)}` : '', match.playerAssignment.startTime ? formatTime(match.playerAssignment.startTime, timeFormat) : '', match.playerAssignment.facility || match.playerAssignment.location || '', match.playerAssignment.courtAssignment ? `Court ${match.playerAssignment.courtAssignment}` : ''].filter(Boolean).join(' · ')}
                    </Typography>
                  </Alert>
                )}
              </Box>
              <CalendarControls item={match} onSendCalendar={onSendCalendar} onStopCalendar={onStopCalendar} />
              <ParticipationSection title="Team Availability" counts={match.availabilityCounts} event={false} onOpen={() => setParticipationKey('')} />
              {match.lineup && <Button size="small" onClick={() => onViewLineup?.(match)} sx={{ alignSelf: 'flex-start', px: 0 }}>View {match.isTentativeLineup ? 'Tentative ' : ''}Lineup ›</Button>}
            </Stack>
          </Box>
        </Collapse>
        <ParticipationDetails open={participationKey !== null} onClose={() => setParticipationKey(null)} title="Team Availability" subtitle={`vs ${match.opponent}`} groups={match.availabilityGroups} keys={['available', 'maybe', 'not_available', 'unknown']} expandedKey={participationKey || undefined} />
      </CardContent>
    </Card>
  );
}

function EventCard({
  event,
  timeFormat,
  defaultExpanded = false,
  onRespond,
  onSendCalendar,
  onStopCalendar,
  activeTeamCount = 1,
  busy = false,
}: {
  event: PlayerEvent;
  timeFormat: TimeFormat;
  defaultExpanded?: boolean;
  onRespond?: (item: ScheduleItem, status: string, guestCount?: number) => void;
  onSendCalendar?: (item: ScheduleItem) => void;
  onStopCalendar?: (item: ScheduleItem) => void;
  activeTeamCount?: number;
  busy?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [participationKey, setParticipationKey] = useState<string | null>(null);
  const response = responseDisplay(event);
  return (
    <Card variant="outlined" sx={{ borderLeft: `5px solid ${teamAccentColor(event.teamColor)}` }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${event.type} ${event.title}. ${response.label}`}
          onClick={() => setExpanded((current) => !current)}
          onKeyDown={(keyboardEvent) => headerKeyboardToggle(keyboardEvent, () => setExpanded((current) => !current))}
          sx={{ cursor: 'pointer', color: 'text.primary', textAlign: 'left', p: 1.25, minHeight: 56, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ minWidth: 0, width: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <PlayerPortalDateTime item={event} timeFormat={timeFormat} />
              {eventTypeIcon(event)}
              <Typography variant="body2" noWrap sx={{ fontWeight: 900, minWidth: 0, flex: 1 }}>{event.title}</Typography>
            </Stack>
            <HeaderActions item={event} expanded={expanded} onToggle={() => setExpanded((current) => !current)} onRespond={onRespond} busy={busy} />
          </Stack>
        </Box>
        {event.allowMaybeRsvp === false && event.ownRsvp === 'Maybe' && (
          <Alert severity="warning" sx={{ mx: 1.5, mb: 1 }}>Please confirm your attendance</Alert>
        )}
        {event.rsvpDeadlineAt && !event.rsvpClosed && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mx: 1.5, mb: 1 }}>RSVP by {formatDeadline(event.rsvpDeadlineAt)}</Typography>}
        {event.rsvpClosed && <Alert severity="info" sx={{ mx: 1.5, mb: 1 }}>RSVP deadline has passed. Contact your captain to make changes.</Alert>}
        <Collapse in={expanded}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Stack spacing={1.25}>
              <Divider />
              <Box>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" variant="outlined" label={event.audienceLabel || 'Team Event'} />
                      {event.audienceScope !== 'workspace' && <TeamContext names={event.teamNames || [event.teamName]} show={activeTeamCount > 1} />}
                    </Stack>
                  </Box>
                  <SendToMeButton item={event} onSendCalendar={onSendCalendar} />
                </Stack>
                <MapsLocation facility={event.facility} location={event.location} mapUrl={event.facilityMapUrl} />
              </Box>
              <EventDescription description={event.description} />
              <CalendarControls item={event} onSendCalendar={onSendCalendar} onStopCalendar={onStopCalendar} />
              {event.selfServiceEnabled && <EventGuestControls event={event} onRespond={onRespond} busy={busy} />}
              <ParticipationSection title={event.audienceScope === 'workspace' ? 'Workspace RSVPs' : 'Team RSVPs'} counts={event.rsvpCounts} event guestSummary={event.guestSummary} onOpen={() => setParticipationKey('')} />
            </Stack>
          </Box>
        </Collapse>
        <ParticipationDetails open={participationKey !== null} onClose={() => setParticipationKey(null)} title={event.audienceScope === 'workspace' ? 'Workspace RSVPs' : 'Team RSVPs'} subtitle={event.title} groups={event.rsvpGroups} guestGroups={event.guestGroups} keys={['going', 'maybe', 'notGoing', 'noResponse']} expandedKey={participationKey || undefined} />
      </CardContent>
    </Card>
  );
}

function PlayingPreferencesPanel({ context, saving, error, onChange, onRetry }: {
  context: PlayerContext['playingPreferences'];
  saving: boolean;
  error: string | null;
  onChange: (patch: Partial<PlayerContext['playingPreferences']['values']>) => void;
  onRetry: () => void;
}) {
  const toleranceOptions = [
    { value: 'no', label: 'Avoid' },
    { value: 'if_needed', label: 'Only if needed' },
    { value: 'yes', label: 'Comfortable' },
  ];
  const { values, relevance } = context;
  return <Stack spacing={2}>
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 900 }}>Playing Preferences</Typography>
      <Typography color="text.secondary">Your playing preferences help your captains plan lineups that work for you. Preferences are considered alongside availability, team needs, and fair playing opportunities.</Typography>
    </Box>
    {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}>{error}</Alert>}
    <Typography variant="caption" color={error ? 'error' : 'text.secondary'} aria-live="polite">{saving ? 'Saving…' : error ? 'Not saved' : 'All changes saved'}</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
      {relevance.singlesDoubles && <Paper variant="outlined" sx={{ p: 2 }}><FormControl fullWidth><InputLabel>Singles or Doubles</InputLabel><Select label="Singles or Doubles" value={values.singlesPreference} onChange={(event) => onChange({ singlesPreference: event.target.value as typeof values.singlesPreference })}><MenuItem value="yes">Prefer singles</MenuItem><MenuItem value="if_possible">No preference</MenuItem><MenuItem value="no">Prefer doubles</MenuItem></Select></FormControl></Paper>}
      {relevance.sameDay && <Paper variant="outlined" sx={{ p: 2 }}><FormControl fullWidth><InputLabel>Multiple matches on the same day</InputLabel><Select label="Multiple matches on the same day" value={values.sameDayTolerance} onChange={(event) => onChange({ sameDayTolerance: event.target.value as typeof values.sameDayTolerance })}>{toleranceOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl></Paper>}
      {relevance.consecutiveDays && <Paper variant="outlined" sx={{ p: 2 }}><FormControl fullWidth><InputLabel>Matches on consecutive days</InputLabel><Select label="Matches on consecutive days" value={values.consecutiveDayTolerance} onChange={(event) => onChange({ consecutiveDayTolerance: event.target.value as typeof values.consecutiveDayTolerance })}>{toleranceOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl></Paper>}
      {relevance.recoveryWindow && <Paper variant="outlined" sx={{ p: 2 }}><TextField fullWidth type="number" label="Minimum recovery between matches (hours)" value={values.minimumRecoveryHours} onChange={(event) => { const value = Number(event.target.value); if (Number.isInteger(value) && value >= 0 && value <= 72) onChange({ minimumRecoveryHours: value }); }} slotProps={{ htmlInput: { min: 0, max: 72, step: 1 } }} /></Paper>}
    </Box>
    {!Object.values(relevance).some(Boolean) && <Alert severity="info">No preference questions currently apply to your active teams and upcoming match formats. Saved preferences remain available when they become relevant again.</Alert>}
  </Stack>;
}

function PlayerLineupCard({ match, timeFormat, expanded, onToggle, activeTeamCount }: { match: PlayerMatch; timeFormat: TimeFormat; expanded: boolean; onToggle: () => void; activeTeamCount: number }) {
  return <Card id={`player-lineup-${match.id}`} variant="outlined" sx={{ borderLeft: `5px solid ${teamAccentColor(match.teamColor)}` }}>
    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
      <Box role="button" tabIndex={0} aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} lineup for ${scheduleTitle(match)}${isPlaying(match) ? '. Playing.' : match.isPlayerTentative ? '. Planned.' : ''}`} onClick={onToggle} onKeyDown={(event) => headerKeyboardToggle(event, onToggle)} sx={{ cursor: 'pointer', p: 1.25, minHeight: 54, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}>
        <Stack direction="row" spacing={1} alignItems="center"><PlayerPortalDateTime item={match} timeFormat={timeFormat} /><SportsTennisIcon fontSize="small" /><Typography variant="body2" noWrap sx={{ fontWeight: 900, flex: 1, minWidth: 0 }}>{scheduleTitle(match)}</Typography>{isPlaying(match) && <PlayingAssignmentIndicator />}{match.isPlayerTentative && <TentativeAssignmentIndicator />}<HeaderExpandButton expanded={expanded} onToggle={onToggle} /></Stack>
      </Box>
      <Collapse in={expanded}>
        <Stack spacing={1.25} sx={{ px: 1.5, pb: 1.5 }}>
          <Divider />
          <Box><TeamContext names={match.teamNames || [match.teamName]} show={activeTeamCount > 1} /><MatchTimes match={match} timeFormat={timeFormat} /><MapsLocation facility={match.facility} location={match.location} mapUrl={match.facilityMapUrl} /></Box>
          <Box><Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>{match.isTentativeLineup ? 'Tentative Lineup' : 'Lineup'}</Typography>{match.isTentativeLineup && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Lineup is tentative and may change.</Typography>}{match.lineup?.lines.length ? <Stack spacing={0.75}>{match.lineup.lines.map((line) => <Box key={line.positionCode}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 1 }}><Typography variant="body2" sx={{ fontWeight: 800, minWidth: 34 }}>{line.displayName}</Typography><Typography variant="body2">{line.players.map((player) => player.name).join(' / ') || 'TBD'}</Typography></Stack>{line.hasOverride && <Typography variant="caption" color="warning.main" sx={{ display: 'block', pl: { sm: '42px' } }}>{line.wasRescheduled ? 'Rescheduled' : 'Alternate schedule'}{line.date ? ` · ${line.date}` : ''}{line.startTime ? ` · ${formatTime(line.startTime, timeFormat)}` : ''}{line.courtAssignment ? ` · Court ${line.courtAssignment}` : ''}</Typography>}</Box>)}</Stack> : <Typography variant="body2" color="text.secondary">Lineup details are not available.</Typography>}</Box>
        </Stack>
      </Collapse>
    </CardContent>
  </Card>;
}

function MonthCalendar({
  items,
  timeFormat,
  cursorDate,
  onCursorDateChange,
  onOpenItem,
  onOpenDay,
}: {
  items: ScheduleItem[];
  timeFormat: TimeFormat;
  cursorDate: Date;
  onCursorDateChange: (date: Date) => void;
  onOpenItem: (item: ScheduleItem) => void;
  onOpenDay: (items: ScheduleItem[]) => void;
}) {
  const monthStart = new Date(cursorDate.getFullYear(), cursorDate.getMonth(), 1);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  const todayKey = isoDate(new Date());
  const itemsByDate = useMemo(() => {
    const map = new Map<string, ScheduleItem[]>();
    items.forEach((item) => {
      if (!map.has(item.date)) map.set(item.date, []);
      map.get(item.date)?.push(item);
    });
    map.forEach((dayItems) => dayItems.sort((left, right) =>
      String(playerVisibleStartTime(left) || '').localeCompare(String(playerVisibleStartTime(right) || ''))
      || `${left.kind}:${left.id}`.localeCompare(`${right.kind}:${right.id}`),
    ));
    return map;
  }, [items]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button size="small" onClick={() => onCursorDateChange(new Date(cursorDate.getFullYear(), cursorDate.getMonth() - 1, 1))}>‹</Button>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>{monthLabel(cursorDate)}</Typography>
          <Button size="small" onClick={() => onCursorDateChange(new Date(cursorDate.getFullYear(), cursorDate.getMonth() + 1, 1))}>›</Button>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Typography key={day} variant="caption" align="center" sx={{ fontWeight: 900, color: 'text.secondary' }}>{day}</Typography>
          ))}
          {days.map((day) => {
            const key = isoDate(day);
            const dayItems = itemsByDate.get(key) || [];
            const visible = dayItems.slice(0, 3);
            const overflow = dayItems.length - visible.length;
            const inMonth = day.getMonth() === cursorDate.getMonth();
            const isToday = key === todayKey;
            return (
              <Paper
                key={key}
                variant="outlined"
                aria-label={`${day.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}: ${dayItems.length} event${dayItems.length === 1 ? '' : 's'}`}
                sx={{
                  minHeight: { xs: 70, sm: 82 },
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: inMonth ? 'background.default' : 'action.hover',
                  borderColor: isToday ? 'primary.light' : 'divider',
                  boxShadow: isToday ? 'inset 0 0 0 1px rgba(25, 118, 210, 0.16)' : 'none',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ minHeight: 18 }}>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: inMonth ? 'text.primary' : 'text.secondary', flexShrink: 0 }}>{day.getDate()}</Typography>
                </Box>
                <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                  {visible.map((item) => <CalendarEventMarker key={`${item.kind}:${item.id}`} item={item} timeFormat={timeFormat} onOpen={onOpenItem} />)}
                  {overflow > 0 && (
                    <Button size="small" sx={{ minWidth: 0, px: 0.5, py: 0, justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => onOpenDay(dayItems)}>
                      +{overflow}
                    </Button>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Box>
      </Stack>
    </Paper>
  );
}

export function AuthenticatedPlayerView({ timeFormat, onExit, exitLabel = 'Exit Player View' }: PlayerViewProps) {
  const { signOut, session } = useAuth();
  const [data, setData] = useState<PlayerContext | null>(null);
  const [branding, setBranding] = useState<PortalBranding>(defaultPortalBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<PlayerPortalTab>(() => window.location.pathname === '/player/fees' ? 'fees' : window.location.pathname === '/player/preferences' ? 'preferences' : 'schedule');
  const [teamFilter, setTeamFilter] = useState('all');
  const [lineupTeamFilter, setLineupTeamFilter] = useState('all');
  const [lineupDateFilter, setLineupDateFilter] = useState<'future' | 'past' | 'all'>('future');
  const [expandedLineupIds, setExpandedLineupIds] = useState<Set<string>>(() => new Set());
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [scheduleViewMode, setScheduleViewMode] = useState<ScheduleViewMode>(() => localStorage.getItem('courtcaptain.player.scheduleViewMode') === 'calendar' ? 'calendar' : 'list');
  const [calendarCursorDate, setCalendarCursorDate] = useState<Date>(() => new Date());
  const [selectedScheduleItem, setSelectedScheduleItem] = useState<ScheduleItem | null>(null);
  const [selectedDayItems, setSelectedDayItems] = useState<ScheduleItem[]>([]);
  const [busyItemKey, setBusyItemKey] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [savingCalendarEmail, setSavingCalendarEmail] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const pendingPreferencePatch = useRef<Partial<PlayerContext['playingPreferences']['values']> | null>(null);
  const preferenceSaveSequence = useRef(0);

  // The App only renders My Player View after AuthContext has established a
  // session. Reuse that session for the first portal request instead of asking
  // the Supabase client to restore it again. On a fresh production deployment,
  // that second read can briefly return null and incorrectly send the portal
  // request without its Authorization header.
  const playerAuthHeader = useCallback(async (): Promise<Record<string, string>> => (
    session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : authHeader()
  ), [session?.access_token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response = await fetch('/api/player/context', { headers: await playerAuthHeader() });
      // A stored access token can become stale between the captain shell
      // loading and the Player View request. Give Supabase one opportunity to
      // renew it, then retry before treating the signed-in player as absent.
      if (response.status === 401) {
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && refreshed.session?.access_token) {
          response = await fetch('/api/player/context', {
            headers: { Authorization: `Bearer ${refreshed.session.access_token}` },
          });
        }
      }
      const result = await response.json().catch(() => ({})) as PlayerContext & { error?: string };
      if (!response.ok || !result.linked) throw new Error(result.error || 'Unable to open My Player View.');
      setData({ ...result, schedule: {
        matches: (result.schedule?.matches || []).map((item) => localizePlayerScheduleItem(item)),
        events: (result.schedule?.events || []).map((item) => localizePlayerScheduleItem(item)),
        lineups: result.schedule?.lineups?.map((item) => localizePlayerScheduleItem(item)),
      } });
      setBranding(normalizePortalBranding(result.branding));
      setCalendarEmail(result.calendar?.email || result.player.email || '');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to open My Player View.');
    } finally {
      setLoading(false);
    }
  }, [playerAuthHeader]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarEmailResult = params.get('calendarEmail');
    if (!calendarEmailResult) return;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('calendarEmail');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    setSnack({
      severity: calendarEmailResult === 'verified' ? 'success' : 'error',
      message: calendarEmailResult === 'verified' ? 'Calendar Email verified.' : 'Calendar Email verification link is invalid or expired.',
    });
    void load();
  }, [load]);

  useEffect(() => applyAuthenticatedPlayerMetadata(branding), [branding]);

  useEffect(() => {
    localStorage.setItem('courtcaptain.player.scheduleViewMode', scheduleViewMode);
  }, [scheduleViewMode]);

  useEffect(() => {
    if (!data) return;
    const currentItems = [...data.schedule.matches, ...data.schedule.events];
    const matchingItem = (item: ScheduleItem) => currentItems.find((candidate) => candidate.kind === item.kind && candidate.id === item.id) || item;
    setSelectedScheduleItem((current) => current ? matchingItem(current) : null);
    setSelectedDayItems((current) => current.map(matchingItem));
  }, [data]);

  const theme = useMemo(() => createPortalTheme(branding), [branding]);
  const baseScheduleItems = useMemo(() => (
    [...(data?.schedule.matches || []), ...(data?.schedule.events || [])]
      .filter((item) => teamFilter === 'all' || (item.teamIds || [item.teamId]).includes(teamFilter))
  ), [data?.schedule.events, data?.schedule.matches, teamFilter]);
  const eventTypeOptions = useMemo(() => {
    const types = new Set<string>();
    baseScheduleItems.forEach((item) => types.add(item.kind === 'match' ? 'Match' : item.type));
    const ordered = ['Match', 'Practice', 'Social', 'Meeting', 'Other'].filter((type) => types.has(type));
    return ordered.map((type) => ({ value: type, label: type === 'Match' ? 'Matches' : type === 'Practice' ? 'Practices' : type === 'Meeting' ? 'Meetings' : type }));
  }, [baseScheduleItems]);
  useEffect(() => {
    if (eventTypeFilter === 'all') return;
    if (!eventTypeOptions.some((option) => option.value === eventTypeFilter)) setEventTypeFilter('all');
  }, [eventTypeFilter, eventTypeOptions]);
  const scheduleItems = useMemo(() => {
    const items = baseScheduleItems
      .filter((item) => eventTypeFilter === 'all' || (eventTypeFilter === 'Match' ? item.kind === 'match' : item.kind === 'event' && item.type === eventTypeFilter))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(playerVisibleStartTime(a) || '').localeCompare(String(playerVisibleStartTime(b) || '')));
    return items;
  }, [baseScheduleItems, eventTypeFilter]);
  const visibleLineups = useMemo(() => data?.schedule.lineups || [], [data?.schedule.lineups]);
  const filteredLineups = useMemo(() => {
    const today = isoDate(new Date());
    return visibleLineups.filter((match) => (lineupTeamFilter === 'all' || (match.teamIds || [match.teamId]).includes(lineupTeamFilter)) && (lineupDateFilter === 'all' || (lineupDateFilter === 'future' ? match.date >= today : match.date < today))).sort((left, right) => lineupDateFilter === 'future' ? left.date.localeCompare(right.date) || String(playerVisibleStartTime(left) || '').localeCompare(String(playerVisibleStartTime(right) || '')) : right.date.localeCompare(left.date) || String(playerVisibleStartTime(right) || '').localeCompare(String(playerVisibleStartTime(left) || '')));
  }, [lineupDateFilter, lineupTeamFilter, visibleLineups]);
  const futureLineupAssignmentCount = useMemo(
    () => visibleLineups.filter((match) => match.date >= isoDate(new Date()) && match.isPlayerInVisibleLineup).length,
    [visibleLineups],
  );
  const hasOutstandingBalance = useMemo(
    () => (data?.fees || []).some((team) => team.amountDue > 0),
    [data?.fees],
  );
  const canExit = Boolean(onExit && data && !data.user.isPlayerOnly);
  const selectedFilterTeam = useMemo(
    () => data?.teams.find((team) => team.id === teamFilter) || null,
    [data?.teams, teamFilter],
  );
  const portalNavItems: Array<{ value: PlayerPortalTab; label: string; icon: ReactNode }> = [
    { value: 'schedule', label: 'Schedule', icon: <CalendarMonthIcon /> },
    { value: 'lineups', label: 'Lineups', icon: <ListAltIcon /> },
    { value: 'rosters', label: 'Team Rosters', icon: <GroupsIcon /> },
    { value: 'preferences', label: 'Playing Preferences', icon: <TuneIcon /> },
    { value: 'history', label: 'My History', icon: <HistoryIcon /> },
    { value: 'fees', label: 'Fees', icon: <PaymentIcon /> },
    { value: 'account', label: 'Account', icon: <AccountCircleIcon /> },
  ];

  const respondToItem = async (item: ScheduleItem, status: string, requestedGuestCount?: number) => {
    const currentStatus = item.kind === 'match' ? item.ownAvailability : item.ownRsvp;
    const currentGuestCount = item.kind === 'event' ? Number(item.ownGuestCount || 0) : undefined;
    const nextGuestCount = item.kind === 'event'
      ? (status === 'Going' || status === 'Maybe' ? Number(requestedGuestCount ?? currentGuestCount ?? 0) : 0)
      : undefined;
    if ((currentStatus === status && currentGuestCount === nextGuestCount) || !data) return;
    const previousData = data;
    const itemKey = `${item.kind}:${item.id}`;
    setBusyItemKey(itemKey);
    setData(updatePlayerScheduleResponse(previousData, item, status, nextGuestCount));
    try {
      const response = await fetch('/api/player/response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await playerAuthHeader()),
        },
        body: JSON.stringify({ kind: item.kind, id: item.id, status, ...(item.kind === 'event' ? { guestCount: nextGuestCount } : {}) }),
      });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to update your response.');
      setSnack({ severity: 'success', message: 'Your response was updated.' });
    } catch {
      setData((current) => current ? updatePlayerScheduleResponse(current, item, currentStatus, currentGuestCount) : current);
      setSnack({ severity: 'error', message: 'Your response could not be updated. Please try again.' });
    } finally {
      setBusyItemKey(null);
    }
  };

  const sendCalendar = async (item: ScheduleItem) => {
    const itemKey = `${item.kind}:${item.id}`;
    setBusyItemKey(itemKey);
    try {
      const response = await fetch('/api/player/calendar/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await playerAuthHeader()),
        },
        body: JSON.stringify({ kind: item.kind, id: item.id }),
      });
      const result = await response.json().catch(() => ({})) as { maskedEmail?: string; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to send calendar event.');
      setSnack({ severity: 'success', message: `Calendar event sent${result.maskedEmail ? ` to ${result.maskedEmail}` : ''}.` });
      await load();
    } catch (sendError) {
      setSnack({ severity: 'error', message: sendError instanceof Error ? sendError.message : 'Unable to send calendar event.' });
    } finally {
      setBusyItemKey(null);
    }
  };

  const stopCalendar = async (item: ScheduleItem) => {
    if (!item.calendarSubscription?.id) return;
    setBusyItemKey(`${item.kind}:${item.id}`);
    try {
      const response = await fetch('/api/player/calendar/stop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await playerAuthHeader()),
        },
        body: JSON.stringify({ subscriptionId: item.calendarSubscription.id }),
      });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to stop calendar updates.');
      setSnack({ severity: 'success', message: 'Calendar updates stopped for this event.' });
      await load();
    } catch (stopError) {
      setSnack({ severity: 'error', message: stopError instanceof Error ? stopError.message : 'Unable to stop calendar updates.' });
    } finally {
      setBusyItemKey(null);
    }
  };

  const saveCalendarEmail = async () => {
    setSavingCalendarEmail(true);
    try {
      const response = await fetch('/api/player/calendar-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await playerAuthHeader()),
        },
        body: JSON.stringify({ calendarEmail }),
      });
      const result = await response.json().catch(() => ({})) as { verified?: boolean; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save Calendar Email.');
      setSnack({ severity: result.verified ? 'success' : 'warning', message: result.verified ? 'Calendar Email saved and verified.' : 'Calendar Email saved. Send a verification link before using it for calendar events.' });
      await load();
    } catch (saveError) {
      setSnack({ severity: 'error', message: saveError instanceof Error ? saveError.message : 'Unable to save Calendar Email.' });
    } finally {
      setSavingCalendarEmail(false);
    }
  };

  const sendCalendarEmailVerification = async () => {
    setSavingCalendarEmail(true);
    try {
      const response = await fetch('/api/player/calendar-email/send-verification', {
        method: 'POST',
        headers: await playerAuthHeader(),
      });
      const result = await response.json().catch(() => ({})) as { alreadyVerified?: boolean; maskedEmail?: string; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to send verification link.');
      setSnack({
        severity: 'success',
        message: result.alreadyVerified
          ? 'Calendar Email is verified.'
          : `Verification link sent${result.maskedEmail ? ` to ${result.maskedEmail}` : ''}.`,
      });
      await load();
    } catch (verificationError) {
      setSnack({ severity: 'error', message: verificationError instanceof Error ? verificationError.message : 'Unable to send verification link.' });
    } finally {
      setSavingCalendarEmail(false);
    }
  };

  const copyPaymentIdentifier = async (identifier: string) => {
    try {
      await navigator.clipboard.writeText(identifier);
      setSnack({ severity: 'success', message: 'Copied.' });
    } catch {
      setSnack({ severity: 'error', message: 'Unable to copy payment identifier.' });
    }
  };

  const savePlayingPreferences = async (patch: Partial<PlayerContext['playingPreferences']['values']>) => {
    if (!data || Object.keys(patch).length === 0) return;
    const previous = data.playingPreferences.values;
    const sequence = ++preferenceSaveSequence.current;
    pendingPreferencePatch.current = patch;
    setPreferenceError(null);
    setSavingPreferences(true);
    setData({ ...data, playingPreferences: { ...data.playingPreferences, values: { ...previous, ...patch } } });
    try {
      const response = await fetch('/api/player/preferences', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(await playerAuthHeader()) }, body: JSON.stringify(patch) });
      const result = await response.json().catch(() => ({})) as { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save playing preferences.');
      if (sequence === preferenceSaveSequence.current) pendingPreferencePatch.current = null;
    } catch (saveError) {
      if (sequence === preferenceSaveSequence.current) {
        setData((current) => current ? { ...current, playingPreferences: { ...current.playingPreferences, values: previous } } : current);
        setPreferenceError(saveError instanceof Error ? saveError.message : 'Unable to save playing preferences.');
      }
    } finally {
      if (sequence === preferenceSaveSequence.current) setSavingPreferences(false);
    }
  };

  const changeTab = (next: PlayerPortalTab) => {
    if (next === 'lineups' && tab !== 'lineups') setExpandedLineupIds(new Set());
    setTab(next);
    window.history.pushState({}, '', next === 'fees' ? '/player/fees' : next === 'preferences' ? '/player/preferences' : '/player');
  };

  const viewLineup = (match: PlayerMatch) => {
    setSelectedScheduleItem(null);
    setLineupTeamFilter('all');
    setLineupDateFilter(match.date >= isoDate(new Date()) ? 'future' : 'past');
    setExpandedLineupIds(new Set([match.id]));
    setTab('lineups');
    window.history.pushState({}, '', '/player');
    window.setTimeout(() => document.getElementById(`player-lineup-${match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">Loading My Player View…</Typography>
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  if (error || !data) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
          <Paper variant="outlined" sx={{ p: 3, maxWidth: 480, textAlign: 'center' }}>
            <SportsTennisIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>My Player View unavailable</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>{error || 'This login is not linked to an active player profile.'}</Typography>
            <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
              {onExit && <Button variant="outlined" onClick={onExit}>{exitLabel}</Button>}
              <Button variant="contained" onClick={() => void signOut()}>Sign Out</Button>
            </Stack>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 9, md: 3 } }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: { xs: 1.25, sm: 2 }, py: { xs: 1, sm: 1.25 } }}>
          <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ maxWidth: 1440, mx: 'auto', minWidth: 0 }}>
            {branding.logoUrl ? (
              <Box
                component="img"
                src={branding.logoUrl}
                alt=""
                sx={{
                  width: { xs: 64, sm: 96, md: 112 },
                  height: { xs: 64, sm: 96, md: 112 },
                  objectFit: 'contain',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            ) : (
              <SportsTennisIcon sx={{ fontSize: { xs: 48, sm: 72, md: 84 }, flexShrink: 0 }} />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{branding.displayName}</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{data.player.displayName}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>My Player View</Typography>
            </Box>
            {canExit && (
              <Button color="inherit" variant="outlined" size="small" onClick={onExit} sx={{ borderColor: 'rgba(255,255,255,0.7)', display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}>
                {exitLabel}
              </Button>
            )}
            <Button color="inherit" variant="text" size="small" startIcon={<LogoutIcon />} onClick={() => void signOut()} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              Sign Out
            </Button>
          </Stack>
        </Box>

        <Box sx={{ maxWidth: 1440, mx: 'auto', p: { xs: 1.5, sm: 2.5 }, display: { md: 'grid' }, gridTemplateColumns: { md: '230px minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
          <Paper component="nav" variant="outlined" aria-label="Player Portal sections" sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 16, p: 1 }}>
            <Stack spacing={0.5}>
              {portalNavItems.map((item) => <Button key={item.value} startIcon={item.icon} variant={tab === item.value ? 'contained' : 'text'} color="primary" onClick={() => changeTab(item.value)} sx={{ justifyContent: 'flex-start', minHeight: 44 }}>{item.label}</Button>)}
            </Stack>
          </Paper>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
          {canExit && (
            <Alert severity="info" sx={{ display: { xs: 'flex', sm: 'none' } }} action={<Button color="inherit" size="small" onClick={onExit}>{exitLabel}</Button>}>
              You are viewing CourtCaptain as a player.
            </Alert>
          )}

          {tab === 'schedule' && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>Schedule</Typography>
                <Typography color="text.secondary">
                  {data.featureControls?.playerEventSelfServiceEnabled ? 'Manage your availability and attendance for enabled teams.' : 'View match availability and team event RSVPs.'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                <ButtonGroup size="small" variant="outlined" aria-label="Schedule view">
                  <Button variant={scheduleViewMode === 'list' ? 'contained' : 'outlined'} onClick={() => setScheduleViewMode('list')}>List</Button>
                  <Button variant={scheduleViewMode === 'calendar' ? 'contained' : 'outlined'} onClick={() => setScheduleViewMode('calendar')}>Calendar</Button>
                </ButtonGroup>
              </Stack>
              {(data.teams.length > 1 || data.schedule.events.length > 0) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  {data.teams.length > 1 && (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Team</InputLabel>
                      <Select
                        label="Team"
                        value={teamFilter}
                        onChange={(event) => setTeamFilter(event.target.value)}
                        renderValue={(value) => value === 'all' || !selectedFilterTeam
                          ? <TeamLabel name="All Teams" />
                          : <TeamLabel name={selectedFilterTeam.shortName || selectedFilterTeam.name} color={selectedFilterTeam.color} />}
                      >
                        <MenuItem value="all"><TeamLabel name="All Teams" /></MenuItem>
                        {data.teams.map((team) => (
                          <MenuItem key={team.id} value={team.id}>
                            <TeamLabel name={team.shortName || team.name} color={team.color} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl size="small" fullWidth>
                    <InputLabel>Event Type</InputLabel>
                    <Select label="Event Type" value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
                      <MenuItem value="all">All Events</MenuItem>
                      {eventTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
              <PlayerPortalScheduleKey />
              <PlayerPortalTeamLegend items={scheduleItems} teamFilter={teamFilter} />
              {scheduleItems.length === 0 ? (
                <Alert severity="info">No upcoming player-visible matches or team events.</Alert>
              ) : scheduleViewMode === 'list' ? (
                <Stack spacing={1.5}>
                  {scheduleItems.map((item) => item.kind === 'match'
                    ? <MatchCard key={`match:${item.id}`} match={item} timeFormat={timeFormat} activeTeamCount={data.teams.length} busy={busyItemKey === `match:${item.id}`} onRespond={respondToItem} onSendCalendar={sendCalendar} onStopCalendar={stopCalendar} onViewLineup={viewLineup} />
                    : <EventCard key={`event:${item.id}`} event={item} timeFormat={timeFormat} activeTeamCount={data.teams.length} busy={busyItemKey === `event:${item.id}`} onRespond={respondToItem} onSendCalendar={sendCalendar} onStopCalendar={stopCalendar} />)}
                </Stack>
              ) : (
                <MonthCalendar
                  items={scheduleItems}
                  timeFormat={timeFormat}
                  cursorDate={calendarCursorDate}
                  onCursorDateChange={setCalendarCursorDate}
                  onOpenItem={setSelectedScheduleItem}
                  onOpenDay={setSelectedDayItems}
                />
              )}
            </Stack>
          )}

          {tab === 'lineups' && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>Lineups</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormControl size="small" fullWidth><InputLabel>Team</InputLabel><Select label="Team" value={lineupTeamFilter} onChange={(event) => setLineupTeamFilter(event.target.value)}><MenuItem value="all">All Teams</MenuItem>{data.teams.map((team) => <MenuItem key={team.id} value={team.id}><TeamLabel name={team.shortName || team.name} color={team.color} /></MenuItem>)}</Select></FormControl>
                <FormControl size="small" fullWidth><InputLabel>Date</InputLabel><Select label="Date" value={lineupDateFilter} onChange={(event) => setLineupDateFilter(event.target.value as 'future' | 'past' | 'all')}><MenuItem value="future">Future</MenuItem><MenuItem value="past">Past</MenuItem><MenuItem value="all">All</MenuItem></Select></FormControl>
              </Stack>
              <PlayerPortalLineupsKey />
              {filteredLineups.length === 0 ? <Alert severity="info">{visibleLineups.length === 0 ? (lineupDateFilter === 'past' ? 'No past lineups are available.' : 'No upcoming lineups are available.') : 'No lineups match these filters.'}</Alert> : filteredLineups.map((match) => <PlayerLineupCard key={match.id} match={match} timeFormat={timeFormat} activeTeamCount={data.teams.length} expanded={expandedLineupIds.has(match.id)} onToggle={() => setExpandedLineupIds((current) => { const next = new Set(current); if (next.has(match.id)) next.delete(match.id); else next.add(match.id); return next; })} />)}
            </Stack>
          )}

          {tab === 'rosters' && (
            <PlayerPortalRosters
              teams={data.teams}
              authHeader={playerAuthHeader}
              notify={(severity, message) => setSnack({ severity, message })}
              openAccount={() => changeTab('account')}
            />
          )}

          {tab === 'fees' && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>Fees</Typography>
                <Typography color="text.secondary">Your current team fees and available account credit.</Typography>
              </Box>
              {data.fees.length === 0 || data.fees.every((team) => team.outstandingFees === 0 && team.accountCredit === 0) ? (
                <Alert severity="success">You&apos;re all caught up. You don&apos;t currently owe any team fees.</Alert>
              ) : data.fees.map((team) => (
                <Card key={team.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>{team.name}</Typography>
                        <Typography variant="h4" color={team.amountDue > 0 ? 'primary' : 'success.main'} sx={{ fontWeight: 900, mt: 0.25 }}>
                          Amount Due: {formatMoney(team.amountDue)}
                        </Typography>
                      </Box>

                      {team.outstandingFees > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Outstanding Fees — {formatMoney(team.outstandingFees)}</Typography>
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            {team.fees.map((fee) => (
                              <Stack key={fee.id} direction="row" spacing={1} justifyContent="space-between" alignItems="baseline">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{fee.description}</Typography>
                                  <Typography variant="caption" color="text.secondary">{formatDate(fee.date)}{fee.status ? ` · ${fee.status}` : ''}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{formatMoney(fee.amount)}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Paper>
                      )}

                      {team.accountCredit > 0 && (
                        <Alert severity="info">
                          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Account Credit — {formatMoney(team.accountCredit)}</Typography>
                          {team.creditAppliedForDisplay > 0 && <Typography variant="body2">{formatMoney(team.creditAppliedForDisplay)} reduces your current balance.</Typography>}
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {team.remainingCredit > 0
                              ? `You have ${formatMoney(team.remainingCredit)} remaining credit. Your captain will apply this credit to your next fee. If you would prefer to have it sent back, contact your captain.`
                              : 'Your captain will apply this credit to your next fee. If you would prefer to have it sent back, contact your captain.'}
                          </Typography>
                        </Alert>
                      )}

                      {team.amountDue > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Pay Your Captain</Typography>
                          {team.paymentMethods.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Contact your captain for payment instructions.</Typography>
                          ) : (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Send your payment using one of the methods your captain accepts.</Typography>
                              <Stack spacing={0.75} sx={{ mt: 1 }}>
                                {team.paymentMethods.map((method) => (
                                  <Stack key={method.id} direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{method.method}</Typography>
                                      <Typography variant="body2">{method.identifier}</Typography>
                                    </Box>
                                    <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => void copyPaymentIdentifier(method.identifier)} aria-label={`Copy ${method.method} payment identifier`}>
                                      Copy
                                    </Button>
                                  </Stack>
                                ))}
                              </Stack>
                            </>
                          )}
                        </Paper>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {tab === 'preferences' && (
            <PlayingPreferencesPanel
              context={data.playingPreferences}
              saving={savingPreferences}
              error={preferenceError}
              onChange={(patch) => void savePlayingPreferences(patch)}
              onRetry={() => { if (pendingPreferencePatch.current) void savePlayingPreferences(pendingPreferencePatch.current); }}
            />
          )}

          {tab === 'history' && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>My History</Typography>
                <Typography color="text.secondary">Personal results and doubles partner history tied to your player record.</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <RecordSummary title="Current Teams" record={data.history.records.currentTeams} />
                <RecordSummary title="All Time" record={data.history.records.allTime} />
              </Stack>
              {data.history.records.byTeamSeason.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>Team / Season Records</Typography>
                  <Stack spacing={1}>
                    {data.history.records.byTeamSeason.map((record) => (
                      <Typography key={`${record.teamName}:${record.seasonName}`} variant="body2">
                        <strong>{record.teamName}</strong>{record.seasonName ? ` · ${record.seasonName}` : ''}: {record.wins}-{record.losses}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              )}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>Doubles Partner History</Typography>
                {data.history.partners.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No doubles partner history yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {data.history.partners.map((partner) => (
                      <Typography key={partner.playerId} variant="body2">
                        <strong>{partner.partnerName}</strong>: {partner.matches} matches · {partner.wins}-{partner.losses}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Paper>
              <Stack spacing={1.25}>
                {data.history.matches.length === 0 ? (
                  <Alert severity="info">No personal match history is available yet.</Alert>
                ) : data.history.matches.slice(0, 50).map((match) => (
                  <Card key={`${match.matchId}:${match.line}`} variant="outlined">
                    <CardContent>
                      <Stack spacing={0.75}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{formatDate(match.date)} vs {match.opponent}</Typography>
                          <Chip size="small" color={statusColor(match.playerResult)} label={match.playerResult} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">{match.teamName}{match.seasonName ? ` · ${match.seasonName}` : ''}</Typography>
                        <Typography variant="body2">{match.line} · {match.scores || 'Score unavailable'}{match.partnerNames.length ? ` · Partner: ${match.partnerNames.join(', ')}` : ''}</Typography>
                        <Typography variant="caption" color="text.secondary">Team result: {match.teamResult}{match.teamScore ? ` ${match.teamScore}` : ''}</Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}

          {tab === 'account' && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      CourtCaptain calendar events and updates will be sent to this address. This does not change your CourtCaptain login email.
                    </Typography>
                  </Box>
                  <TextField
                    label="Calendar Email"
                    type="email"
                    value={calendarEmail}
                    onChange={(event) => setCalendarEmail(event.target.value)}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Button variant="contained" onClick={() => void saveCalendarEmail()} disabled={savingCalendarEmail}>
                      Save Calendar Email
                    </Button>
                    {!data.calendar?.verified && (
                      <Button variant="outlined" onClick={() => void sendCalendarEmailVerification()} disabled={savingCalendarEmail}>
                        Send Verification Link
                      </Button>
                    )}
                    <Chip
                      size="small"
                      color={data.calendar?.verified ? 'success' : 'warning'}
                      label={data.calendar?.verified ? 'Verified' : 'Verification required before sending'}
                    />
                  </Stack>
                </Stack>
              </Paper>
              <AccountManagement />
            </Stack>
          )}
          </Stack>
        </Box>

        <Dialog open={Boolean(selectedScheduleItem)} onClose={() => setSelectedScheduleItem(null)} fullWidth maxWidth="sm">
          <DialogTitle>{selectedScheduleItem ? scheduleTitle(selectedScheduleItem) : 'Schedule Detail'}</DialogTitle>
          <DialogContent dividers>
            {selectedScheduleItem?.kind === 'match' && <MatchCard match={selectedScheduleItem} timeFormat={timeFormat} activeTeamCount={data.teams.length} defaultExpanded busy={busyItemKey === `match:${selectedScheduleItem.id}`} onRespond={respondToItem} onSendCalendar={sendCalendar} onStopCalendar={stopCalendar} onViewLineup={viewLineup} />}
            {selectedScheduleItem?.kind === 'event' && <EventCard event={selectedScheduleItem} timeFormat={timeFormat} activeTeamCount={data.teams.length} defaultExpanded busy={busyItemKey === `event:${selectedScheduleItem.id}`} onRespond={respondToItem} onSendCalendar={sendCalendar} onStopCalendar={stopCalendar} />}
          </DialogContent>
        </Dialog>

        <Dialog open={selectedDayItems.length > 0} onClose={() => setSelectedDayItems([])} fullWidth maxWidth="xs">
          <DialogTitle>{selectedDayItems[0] ? formatDate(selectedDayItems[0].date) : 'Schedule'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1}>
              {selectedDayItems.map((item) => (
                <ScheduleItemPill
                  key={`${item.kind}:${item.id}`}
                  item={item}
                  timeFormat={timeFormat}
                  onOpen={(nextItem) => {
                    setSelectedDayItems([]);
                    setSelectedScheduleItem(nextItem);
                  }}
                />
              ))}
            </Stack>
          </DialogContent>
        </Dialog>

        <Paper
          elevation={8}
          sx={{
            position: { xs: 'fixed', md: 'sticky' },
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            maxWidth: { md: 1440 },
            mx: { md: 'auto' },
            borderRadius: { xs: 0, md: 3 },
            overflowX: 'auto',
            display: { xs: 'block', md: 'none' },
          }}
        >
          <BottomNavigation value={tab} onChange={(_, next) => changeTab(next)} showLabels sx={{ minWidth: 560, '& .MuiBottomNavigationAction-root': { minWidth: 72, px: { xs: 0.25, sm: 1 }, flex: 1 }, '& .MuiBottomNavigationAction-label': { fontSize: { xs: '0.62rem', sm: '0.75rem' } } }}>
            <BottomNavigationAction label="Schedule" value="schedule" icon={<CalendarMonthIcon />} />
            <BottomNavigationAction
              label="Lineups"
              value="lineups"
              icon={<Badge color="error" badgeContent={futureLineupAssignmentCount} max={9} invisible={futureLineupAssignmentCount === 0}><ListAltIcon /></Badge>}
              aria-label={futureLineupAssignmentCount > 0 ? `Lineups: ${futureLineupAssignmentCount} upcoming assignment${futureLineupAssignmentCount === 1 ? '' : 's'}` : 'Lineups'}
            />
            <BottomNavigationAction label="Rosters" value="rosters" icon={<GroupsIcon />} />
            <BottomNavigationAction label="Preferences" value="preferences" icon={<TuneIcon />} />
            <BottomNavigationAction label="History" value="history" icon={<HistoryIcon />} />
            <BottomNavigationAction
              label="Fees"
              value="fees"
              icon={<Badge color="error" variant="dot" invisible={!hasOutstandingBalance}><PaymentIcon /></Badge>}
              aria-label={hasOutstandingBalance ? 'Fees: outstanding balance' : 'Fees'}
            />
            <BottomNavigationAction label="Account" value="account" icon={<AccountCircleIcon />} />
          </BottomNavigation>
        </Paper>
        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={5000}
          onClose={() => setSnack(null)}
          message={snack?.message || ''}
        />
      </Box>
    </ThemeProvider>
  );
}
